import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail as fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Megaphone, Pin, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { timeAgo } from "@/lib/ui";

export default function AnnouncementsTab({ canManage }) {
  const [list, setList] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const load = useCallback(() => api.get("/hr/announcements").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Title and message are required"); return; }
    try { await api.post("/hr/announcements", { title, body, pinned }); toast.success("Announcement posted — team notified"); setTitle(""); setBody(""); setPinned(false); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const pin = async (a) => { await api.patch(`/hr/announcements/${a.id}`, { pinned: !a.pinned }); load(); };
  const remove = async (a) => { if (!window.confirm("Delete this announcement?")) return; await api.delete(`/hr/announcements/${a.id}`); load(); };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="announcements-tab">
      {canManage && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2.5" data-testid="announcement-form">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="announcement-title-input" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write an update for the team…" data-testid="announcement-body-input" />
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm text-slate-600 flex items-center gap-2"><Switch checked={pinned} onCheckedChange={setPinned} data-testid="announcement-pin-switch" />Pin to top</label>
            <Button onClick={post} className="bg-indigo-600 hover:bg-indigo-700" data-testid="announcement-post-button"><Megaphone className="w-4 h-4 mr-1.5" />Post</Button>
          </div>
        </div>
      )}
      {list.map((a) => (
        <article key={a.id} className={`bg-white border rounded-xl p-5 ${a.pinned ? "border-indigo-200 ring-1 ring-indigo-100" : "border-slate-200"}`} data-testid={`announcement-${a.id}`}>
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold text-slate-900 flex items-center gap-1.5">{a.pinned && <Pin className="w-3.5 h-3.5 text-indigo-500" />}{a.title}</p>
              <p className="text-xs text-slate-400 mt-0.5">{a.author_name} · {timeAgo(a.created_at)}</p>
            </div>
            {canManage && (
              <>
                <button onClick={() => pin(a)} title={a.pinned ? "Unpin" : "Pin"} className={a.pinned ? "text-indigo-500" : "text-slate-300 hover:text-indigo-500"} data-testid={`announcement-pin-${a.id}`}><Pin className="w-4 h-4" /></button>
                <button onClick={() => remove(a)} className="text-slate-300 hover:text-red-500" data-testid={`announcement-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
              </>
            )}
          </div>
          <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap leading-relaxed">{a.body}</p>
        </article>
      ))}
      {!list.length && <p className="text-sm text-slate-400 py-8 text-center">No announcements yet.</p>}
    </div>
  );
}
